import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { CreditCard, AlertCircle, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useMembership } from "@/hooks/useMembership";

interface Job {
  id: string;
  title: string;
  description: string;
  location: string;
  agreed_cost?: number;
}

interface PaymentDialogProps {
  job: Job | null;
  open: boolean;
  onClose: () => void;
  onPaymentSuccess: () => void;
}

export const PaymentDialog = ({ job, open, onClose, onPaymentSuccess }: PaymentDialogProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { isMember } = useMembership();

  if (!job || !job.agreed_cost) {
    return null;
  }

  const round2 = (n: number) => Math.round(n * 100) / 100;
  // agreed_cost is stored as the tradesman's quote + 3% booking fee
  const tradesmanQuote = round2(job.agreed_cost / 1.03);
  const jobCost = round2(tradesmanQuote * 1.03); // total quoted to customer
  const bookingFee = isMember ? 0 : round2(tradesmanQuote * 0.03); // 3% booking fee (waived for members)
  const deposit = round2(tradesmanQuote * 0.07); // 7% deposit
  const platformCharge = round2(bookingFee + deposit); // Total platform charge
  const tradesmanAmount = round2(tradesmanQuote * 0.93); // 93% paid on completion

  const handlePayment = async () => {
    setIsLoading(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        throw new Error("You must be logged in to make a payment");
      }
      
      const accessToken = session?.access_token;

      const { data, error } = await supabase.functions.invoke('create-payment', {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
        body: {
          job_id: job.id,
          agreed_cost: job.agreed_cost,
          platform_charge: platformCharge,
          origin: window.location.origin
        }
      });

      if (error) {
        throw error;
      }

      if (data?.url) {
        // Open Stripe checkout in a new tab
        window.open(data.url, '_blank');
        
        toast({
          title: "Redirecting to payment",
          description: "Please complete your payment in the new tab."
        });
        
        onClose();
      } else {
        throw new Error("Payment URL not received");
      }
    } catch (error: any) {
      toast({
        title: "Payment error",
        description: error.message || "Failed to process payment",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-primary" />
            Complete Payment
          </DialogTitle>
          <DialogDescription>
            Finalize your job request with secure payment processing
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Job Details */}
          <div className="bg-muted p-4 rounded-lg">
            <h3 className="font-semibold mb-2">{job.title}</h3>
            <p className="text-sm text-muted-foreground mb-2">{job.location}</p>
            <p className="text-sm text-muted-foreground">{job.description}</p>
          </div>

          {/* Payment Amount */}
          <div className="space-y-3">
            <div className="bg-blue-50 p-3 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span>Job cost (quoted):</span>
                <span>£{jobCost.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Booking fee (3%){isMember ? " — waived with membership" : ""}:</span>
                <span>£{bookingFee.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Deposit (7%):</span>
                <span>£{deposit.toFixed(2)}</span>
              </div>
              <div className="border-t pt-2">
                <div className="flex justify-between font-semibold text-lg">
                  <span>Platform Charge:</span>
                  <span>£{platformCharge.toFixed(2)}</span>
                </div>
              </div>
              <div className="bg-green-50 p-2 rounded border-t">
                <div className="flex justify-between text-sm text-green-700">
                  <span>Tradesman receives directly:</span>
                  <span>£{tradesmanAmount.toFixed(2)} (93%)</span>
                </div>
              </div>
            </div>
          </div>

          {/* Important Notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-amber-800 mb-1">Payment Structure</p>
                <p className="text-amber-700">
                  {isMember
                    ? "Your membership waives the 3% booking fee — you only pay the 7% deposit to secure the job. The tradesman receives 93% of the quoted amount directly from you upon completion."
                    : "You pay a booking fee (3%) + deposit (7%) to secure the job. The tradesman receives 93% of the quoted amount directly from you upon completion."}
                </p>
              </div>
            </div>
          </div>

          {/* Payment Button */}
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={onClose}
              disabled={isLoading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button 
              onClick={handlePayment}
              disabled={isLoading}
              className="flex-1"
              variant="hero"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Pay £{platformCharge.toFixed(2)}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};